import * as THREE from 'three';
import { DEPTH_ZONES } from '../sim/seafloor';
import { CORAL_RATES } from '../sim/coral';
import { lightShare } from '../sim/light';
import { COAST_SHARE, COLS, LAND_SHARE_PER_M, ROWS, TANK, type Exhibit, type Habitat } from './exhibit';
import { hash3 } from './fishSwim';
import { attachSkin, type CausticUniforms, type ExhibitPaint, type FogUniforms } from './skin';
import { SKIN_TILE_PX } from './skinTiles';
import { BLEACHED_CORAL, CAUSTIC_CONTRAST, CAUSTIC_DIRECTIONS, LIVE_CORAL, label } from './aquariumView';
import { placeRulerLabels } from './depthAxis';
import { glowAt, sunDirection, type DaySun, type HourSun } from './sunlight';
import { FishSchools, type Visible, UNITS_PER_MM, type KindCounts } from './fishSchools';
import { FISH_KINDS } from './fishKinds';
import { speciesOf } from '../sim/species';
import { TagMarks, schoolMark, standIn, type MarkTarget } from './tagMarks';
import { ReefOutcrops, outcropPlaces, seatedPlaces, tallestM, type ReefModel, type ReefTextures } from './reefOutcrops';
import { ExhibitWhales, WHALE_DRAW } from './whales';
import { ExhibitTurtles, HONU_DRAW, honuHours, type HonuDrawn, type HonuSprite } from './turtles';
import { seenBy, tapNearness, type Pickable } from './pick';
import { ExhibitSeals, SEAL_DRAW, type SealDrawn, type SealSprite } from './seals';

/**
 * The aquarium as one giant exhibit, drawn (M94, `docs/AQUARIUM_REDESIGN.md`;
 * its shape is `exhibit.ts`'s): a wide tank seen from the side through clear
 * glass, the island at the left, the reef, the slope, the twilight water and
 * the deep to the right, the water darkening with depth behind it.
 *
 * - **The floor** in pixel-art palettes (`skin.ts`'s exhibit paint): basalt
 *   where the real floor is steep, sand going to silt with depth, the reef's
 *   pavement tinted by its coral and its bleaching (`setReef`) and flecked
 *   with coralline; the islands' land keeps its tile. Rock or sand is a
 *   rendering choice by the real slope, said so: the game knows no substrate
 *   (`AQUARIUM_ART.md` 1.1).
 * - **The light**: each part of the floor lit by the day's sunlight at its
 *   real depth, by the measured clarity (`setDay`), drawn on a log scale, as
 *   an eye sees it, with a floor of `VISIBLE` so the deep can be seen at all
 *   (a rendering choice, never the light the simulation reckons); its reds
 *   fade first with depth (`FADE_PER_M`). Caustics on the shallows, as on the
 *   map.
 * - **The water**: clear at the front, a gentle tint on the side, and behind
 *   it the water's own color by depth, glowing where the day's light reaches;
 *   fog through it, bluer near the surface and darker and thicker in the deep.
 *   No ripple, no sun's disc or path: they are the map's.
 */

const { width: W, height: H, depth: D } = TANK;
const TOP = H / 2;
const BOTTOM = -H / 2;

/** Scene units a texel of the floor's paint spans (JUDGED: a 1,024th of the tank's width, the map's own texel near enough). */
const EXHIBIT_TEXEL = W / 1024;

/**
 * The exhibit's sprites (JUDGED, by eye at its opening view): a fish sprite for 625 fish, the map's own (the
 * diorama's 1,250 were the user's word for that view, M86); drawn twice the map's length, so a reef fish on the
 * reef's focus is about 30 px long on a computer; an outcrop for 10 km² of reef floor (the map's 16), its widest
 * 0.65 units across: the largest that leaves every island's band room for its whole share, each with its tallest
 * outcrop under the surface (MEASURED: at 0.7 units Molokaʻi's band held 20 of its 21, at 0.8 units 18, at 0.9 and
 * 4 km² 14 of 52); every fish at least 8 px long, however far the camera stands (M97, JUDGED, a rendering choice:
 * the whole tank on a phone held upright draws a bullethead 3 px long).
 */
export const EXHIBIT_SPRITES = { fishPerSprite: 625, scale: 2, outcropUnits: 0.65, km2PerOutcrop: 10, minPx: 8 } as const;

/**
 * The zooplankton as specks through the mixed layer (M97, the brief's section 5: "plankton as a haze or particles"):
 * a speck for a fiftieth of a millimole of nitrogen a square meter of the column (JUDGED, a rendering choice: Maui
 * Nui's 4.4 to 9.0 mmol N m⁻² over its first year, MEASURED, draw 220 to 450), at most `most`, each inside the mixed
 * layer. No vertical migration is drawn: none was read for Hawaiʻi's zooplankton that the game may carry (M97, open:
 * BCO-DMO's day and night tows, CC BY 4.0, are on a host this environment refuses). Each `px` pixels across, at
 * `opacity` by day, in clumps of `perClump` about `clumpUnits` across (M105, JUDGED: the redesign v2's "smaller on
 * screen, dimmer, lower in contrast, not spread evenly"; M97's 2.5 px at 0.55, spread evenly, read as confetti).
 */
const PLANKTON = { specksPerMmolNM2: 50, most: 2000, px: 1.5, opacity: 0.35, perClump: 25, clumpUnits: 0.6 } as const;

/** How far a ray of `shotComposition` is marched a step, scene units (JUDGED: under a fish's length). */
const MARCH_UNITS = 0.05;

/** The least light the floor is drawn by, a share of full (JUDGED, a rendering choice: the deep and the night seen at all). */
export const VISIBLE = 0.2;

/**
 * The exhibit's rendering choices, each said, for the panel's Drawn row (M104, the brief's section 8: "simulation
 * values, estimates, visual representations, exaggerated geometry and cinematic behavior told apart"), from the
 * figures that set them.
 */
export function exhibitChoices(): string {
  const [first] = FISH_KINDS;
  // The kinds drawn a sprite for more fish than the rest (the roi's, M146; the taʻape's, M233, M252), by their Hawaiian
  // names, each with its own.
  const many = FISH_KINDS.filter((k) => k.perSprite !== undefined && k.perSprite !== EXHIBIT_SPRITES.fishPerSprite);
  const per = [...new Set(many.map((k) => k.perSprite as number))];
  const n = (x: number): string => x.toLocaleString('en-US');
  return [
    `a fish sprite for ${n(EXHIBIT_SPRITES.fishPerSprite)} fish${many.length ? ` (${per.map((x) => `${many.filter((k) => k.perSprite === x).map((k) => `the ${speciesOf(k.species).hawaiian}'s`).join(' and ')} for ${n(x)}`).join(', ')})` : ''}, ${EXHIBIT_SPRITES.scale} times its length on the map and never under ${EXHIBIT_SPRITES.minPx} pixels, a school's way between shelter and feeding drawn ${first?.swim.wander ?? 1} times as long (the roi's and the ʻōmilu's patches and the ulua's range as they are)`,
    `a honu for ${n(HONU_DRAW.turtlesPerSprite)}, to the fish's scale`,
    `a monk seal for ${n(SEAL_DRAW.sealsPerSprite)}, drawn shorter than the fish's scale would`,
    `a whale for ${n(WHALE_DRAW.whalesPerSprite)}, drawn far shorter than the fish's scale would`,
    `each outcrop ${EXHIBIT_SPRITES.km2PerOutcrop} km² of reef`,
    `each speck of plankton 1/${PLANKTON.specksPerMmolNM2} of a millimole of nitrogen a square meter`,
    `the deep and the night drawn brighter than they are, never under ${Math.round(VISIBLE * 100)}% of full light`,
  ].join('; ');
}
/**
 * How fast the floor's red, green and blue fade with its real depth, per meter, to `FADE_TO_M` (JUDGED, set by eye
 * so the reef keeps its colors to 30 m and a floor at 100 m reads blue; water takes red first, then green).
 */
export const FADE_PER_M: readonly [number, number, number] = [0.03, 0.012, 0.004];
export const FADE_TO_M = 150;

/** The palettes (JUDGED, the brief's: muted blue-gray basalt, pale warm sand, pink coralline; silt grayer in the deep), sRGB. */
export const PAINT_HEX = {
  sand: [0xe6d6ac, 0xd9c697, 0xccb783, 0xf0e3bf],
  silt: [0x8a8578, 0x7b776b, 0x6c685e, 0x96917f],
  basalt: [0x5d6b7d, 0x4e5b6c, 0x707e8f, 0x414d5c],
  coralline: [0xd98ca2, 0xbf6f8c],
} as const;
/** Rock begins where the real floor slopes 3°, and is all rock at 8° (JUDGED: the 1 km medians' gentle shelves sand, their drop-offs rock). */
export const ROCK_DEG: readonly [number, number] = [3, 8];
/** Sand gives way to silt between 40 m and 400 m (JUDGED: pale sand on the reef and its slope, gray silt below). */
export const SILT_M: readonly [number, number] = [40, 400];

/** The reef's pavement's rubble (JUDGED), tinted toward the map's live and bleached coral (`aquariumView.ts`). */
const RUBBLE = new THREE.Color(0xb8a588);
/** How far the reef's pavement goes toward live coral at the reef's ceiling of cover (JUDGED). */
const MOST_TINT = 0.55;

/** The water's color behind the tank by real depth, sRGB (JUDGED: turquoise over the reef to near black in the deep), and lit by the day. */
const BACKDROP: readonly (readonly [number, number])[] = [[0, 0x1f8cbf], [30, 0x1873a3], [200, 0x0c4672], [1000, 0x05223c], [4000, 0x02080f]];
const LIT = 0x4cb3df;
/** The fog: its color near the surface and in the deep, where it is darkest, and how dense, per scene unit, there and in the deep (JUDGED). */
const FOG_TOP = 0x1f86b4;
const FOG_DEEP = 0x030c16;
const FOG_DENSITY = 0.045;
const FOG_DEEP_DENSITY = 2.5;
/** The glass at the front and the side, and the surface: how opaque (JUDGED: unobtrusive, the brief's). */
const FRONT_OPACITY = 0.04;
const SIDE_OPACITY = 0.07;
const SURFACE_OPACITY = 0.12;
/**
 * The surface seen from under the water (M105, the redesign v2's "water above and behind" a reef seen from within):
 * past 48.6° from overhead, the critical angle of seawater's index of refraction, about 1.33, light from below is all
 * reflected, so the surface mirrors the water under it; drawn as the water's color at the top, lit by the hour as the
 * backdrop is, nearly opaque (JUDGED); the window overhead, where the sky shows, is not drawn, as the camera's tilt
 * (`EXHIBIT_POLAR`) keeps a view from looking up through it.
 */
const UNDERSIDE_OPACITY = 0.9;
/**
 * How bright the cross-section is drawn at the tank's floor and at its top (JUDGED: darker than the floor, so it frames
 * the habitats; M105, lighter than M94's 0.22 to 0.5, the redesign v2's "no uniform dark walls", so its basalt's drifts
 * and ledges are seen).
 */
const CUT_SHADE: readonly [number, number] = [0.35, 0.7];
/** The cross-section's texel, scene units (JUDGED: big enough to stay crisp at the overview, where finer ones shimmered in streaks). */
const CUT_TEXEL = 0.16;
/**
 * The cross-section's basalt varied broadly (M105, the redesign v2's deep floor: "large-scale blue-gray variation",
 * "occasional ledges", "no uniform dark walls"), each JUDGED: smooth drifts, a cell `CUT_BROAD` texels across and
 * down, from `CUT_DRIFT.dark` to `.light` times its color (bluer and lighter); and a ledge, a flow's top lit by
 * `CUT_LEDGE.lift`, over `CUT_LEDGE.share` of runs `CUT_LEDGE.texels` long.
 */
const CUT_BROAD: readonly [number, number] = [14, 6];
const CUT_DRIFT = { dark: [0.85, 0.88, 0.92], light: [1.1, 1.2, 1.38] } as const;
const CUT_LEDGE = { texels: 9, share: 0.3, lift: 1.25 } as const;
/** How bright the water behind the tank is drawn at night, a share of its color by day (JUDGED: dark, but its depths still told apart). */
const NIGHT_BACKDROP = 0.3;
/** The key light at full sunlight (the map's). */
export const KEY_AT_FULL_SUN = 1.4;
/**
 * The light by the hour beyond the sun's beam (M104, the redesign's 6.4 and 3.4), each a rendering choice:
 * - `moonDrawn`: a full moon overhead drawn as this share of full sunlight on the log scale the floor is lit by
 *   (JUDGED: far brighter than real moonlight, so a moonlit night is seen, as the hour's panel says: "Night drawn
 *   brighter than it is"); its phase and height the real ones (`moonAtHour`).
 * - `moonKey`: the key light under a full moon overhead, a share of the sun's (JUDGED), `moonColor` cool.
 * - `warmBelowDeg`, `warmColor`, `warmTint`: the sun warmer as it nears the horizon, below this height, toward this
 *   color; the animals and outcrops tinted with it, their green and blue down by these shares at the horizon (JUDGED:
 *   "dimmer and warmer near the surface" at dusk, the brief's).
 */
const NIGHT_LIGHT = { moonDrawn: 1e-3, moonKey: 0.15, moonColor: 0xc8d4ff } as const;
export const WARM = { belowDeg: 15, sunColor: 0xfff4e0, color: 0xffb070, tint: [0.12, 0.3] } as const;
/**
 * The water's fog and color by its measured state (M104, the redesign's 3.4 and rule 7): the fog thicker as the water
 * is less clear, by its measured clarity against `kdRef` (JUDGED, about Maui Nui's: its region's 0.065 to 0.0791 m-1
 * by month, `light.ts`); the water greener toward `green` with the simulated chlorophyll, none at `chlClear` mg m-3
 * and the most, `mostGreen` of the way, at `chlGreen` (JUDGED: the open ocean's blue to a coast's green water).
 */
const WATER_STATE = { kdRef: 0.07, chlClear: 0.1, chlGreen: 3, mostGreen: 0.35, green: 0x2f7f6a } as const;
/**
 * The depth whose light the fish are seen by, meters (JUDGED: the middle of the reef's band they swim over, 1 to
 * 30 m; by day the band's top and foot differ by about a tenth of it on the log scale at Maui Nui's clarity).
 */
const FISH_LIGHT_M = 15;

/**
 * A camera's frame: the point it looks at and the points that must fit the view; or a shot (M105), composed: the way
 * it is looked at from (`direction`, toward the camera) and the half-height the view holds at the target whatever the
 * screen's shape, so what lies below the target and what above keep their shares of the view (the points then the
 * shot's own, for the ruler); or a panorama (`tall`, M105), its height filling the view and its width as far as that
 * goes, but never more than `tall` times closer than filling the view with all of it.
 */
export interface Frame {
  target: readonly [number, number, number];
  points: readonly (readonly [number, number, number])[];
  direction?: readonly [number, number, number];
  half?: number;
  tall?: number;
}

/**
 * The whole tank (M105, the redesign v2's section 9: "On a narrow phone the full width need not be shown large: a
 * panorama the player pans across, Fit All showing it all smaller"): its height filling the view, never more than
 * `mostCloser` times as close as Fit All's (JUDGED: at least half its width in view, the shallows to the deep read as
 * one descent).
 */
const WHOLE_TANK = { mostCloser: 2 } as const;

/**
 * Each habitat's shot (M105, the redesign v2's section 3: each its own composition, not a box fitted), each part a
 * rendering choice kept from a search of about 2,000 shots a habitat scored by their composition (`shotComposition`) at
 * its worst over the shapes the panels leave of a screen, 1:2 upright to 2.2:1 on its side (JUDGED from MEASURED):
 *
 * - `across`, `back`: where it looks, a share of the way across the habitat's box from its coast end, and a share of
 *   the tank's depth behind its middle.
 * - `aboveFloor`: the target's height over the floor there, a share of the way up to the habitat's top (the surface for
 *   the reef and the blue water).
 * - `direction`: the way back to the camera, toward the front.
 * - `half`: the view's half-height at the target, scene units.
 *
 * The **reef** "a low three-quarter underwater view", 35 to 45% of it reef and 55 to 65% water, "about 3 to 5 times
 * closer" than M104's: past the front band's reef, as a camera this close to it looks down into the cutaway under the
 * front glass (M104's shot was 42% that rock on a phone), from about 10° round toward the tank's middle and 10° above
 * level; 7.1 times closer than M104's on a phone (its distance 4.9 to 34.7) and 2.9 times on a computer (7.1 to 20.6),
 * MEASURED. The **upper slope** "rocky ledges, sparse life on the bottom, darker blue water", its floor 40 to 55% of
 * the view, looked across from 30° round; the **blue water** "spacious open water, its large animals clearly
 * visible", 85% water or more, level, from 20° round, near the surface at its coastward edge, where the humpbacks
 * swim within the 183 m contour (`WHALE_DRAW`; MEASURED, about one of the first day's three in view over ten minutes
 * of their swims), wide enough that a whale's 2.4 units are under three quarters of a phone's width (`NEAR_FADE`); the **twilight** "deep blue, suspended schools", 90% water or more, level; the
 * **deep ocean** "dark seafloor, sparse bottom animals", its floor 45 to 60%, from 30° round, level.
 */
export const HABITAT_SHOTS = {
  reef: { across: 0.8, back: 0.125, aboveFloor: 0.25, direction: [0.18, 0.18, 1], half: 1.5 },
  slope: { across: 0.2, back: 0.25, aboveFloor: 0.4, direction: [0.58, 0.2, 1], half: 1.5 },
  blue: { across: -0.15, back: 0.25, aboveFloor: 0.8, direction: [-0.36, 0, 1], half: 2.2 },
  twilight: { across: 0.4, back: 0.25, aboveFloor: 0.8, direction: [-0.18, 0, 1], half: 1.5 },
  deep: { across: 0.2, back: 0.25, aboveFloor: 0.6, direction: [0.58, 0, 1], half: 1.5 },
} as const;

/**
 * The way the exhibit is looked at: from the front, a little above and a little toward the tank's middle, so a
 * habitat at an end is seen across the tank and not through its end's glass (JUDGED, the brief's "side-on,
 * slightly elevated three-quarter"); the whole tank from a little to the right.
 */
export function exhibitLook(frame: Frame): readonly [number, number, number] {
  return frame.direction ?? [frame.target[0] > 0 ? -0.32 : 0.32, 0.3, 1];
}

/** A color from stops by a value, between them straight. */
function rampColor(stops: readonly (readonly [number, number])[], value: number, out: THREE.Color): THREE.Color {
  const first = stops[0] as readonly [number, number];
  if (value <= first[0]) return out.setHex(first[1]);
  for (let i = 1; i < stops.length; i++) {
    const [v1, c1] = stops[i] as readonly [number, number];
    const [v0, c0] = stops[i - 1] as readonly [number, number];
    if (value <= v1) return out.setHex(c0).lerp(new THREE.Color(c1), (value - v0) / (v1 - v0));
  }
  return out.setHex((stops[stops.length - 1] as readonly [number, number])[1]);
}

export class ExhibitView {
  readonly group = new THREE.Group();
  /** The tagged animals and the named schools marked (M156). */
  readonly marks = new TagMarks();
  /** The camera and the part of the screen the animals were last fitted for (`fitLabels`), and the one each tag's mark last stood over (`standIn`, M161). */
  private markCamera: THREE.Camera | null = null;
  private markVisible: Visible | undefined;
  private readonly marked = new Map<string, number>();
  /** The sphere holding it all, for the camera's farthest. */
  readonly radius: number;
  /** The tank's corners, its top raised to the highest land: the overview's frame. */
  readonly corners: readonly (readonly [number, number, number])[];
  /** How it is drawn, for the sanctuary card. */
  readonly scaleNote: string;
  readonly habitats: readonly Habitat[];
  private readonly key = new THREE.DirectionalLight(WARM.sunColor, KEY_AT_FULL_SUN);
  private readonly positions: Float32Array;
  private readonly floorColors: THREE.BufferAttribute;
  private readonly water: THREE.BufferAttribute;
  private readonly caustic: THREE.BufferAttribute;
  private readonly causticDrift: CausticUniforms['uCausticDrift'];
  private readonly backdrop: { mesh: THREE.Mesh; colors: THREE.BufferAttribute; base: Float32Array; depthM: Float32Array };
  private readonly floorMesh: THREE.Mesh;
  private readonly surface: { uTime: { value: number } };
  /** The surface's underside (`UNDERSIDE_OPACITY`); set in `surfaceOf`. */
  private underside!: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly fish: FishSchools;
  private readonly whales: ExhibitWhales;
  private readonly turtles: ExhibitTurtles;
  private readonly seals: ExhibitSeals;
  /** The cutaway's faces, the tank's walls of rock under its floor. */
  private readonly cut: THREE.Mesh;
  /** The hour civil twilight ends on the day drawn (`setDusk`); 18:30 until it is set (JUDGED, near Maui Nui's). */
  private duskHour = 18.5;
  private readonly plankton: THREE.Points;
  /** The specks drawn now, and the mixed layer they were laid through. */
  private planktonFor = '';
  private outcrops: ReefOutcrops | null = null;
  /** Every fogged surface's fog, for the water's state (`setWater`); the backdrop's green share; the light last laid on the animals, for outcrops come later. */
  private readonly fogged: Partial<FogUniforms>[] = [];
  private greenness = 0;
  private lastTint: [number, number, number] = [1, 1, 1];
  private lastCover: { cover: readonly number[]; bleached: readonly number[]; killed: readonly number[] } | null = null;
  private readonly ruler: THREE.Group;
  private readonly rulerPoint = new THREE.Vector3();
  private readonly rulerFor = new Float64Array(18).fill(Number.NaN);
  private labelsFitFor = 0;
  private landTop = 0;

  /** @param bandNames each band's island's name, back to front, as `exhibit.bands` */
  constructor(private readonly exhibit: Exhibit, bandNames: readonly string[]) {
    const n = COLS * ROWS;
    this.positions = new Float32Array(n * 3);
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const i = r * COLS + c;
        const e = exhibit.elevationM[i] as number;
        this.positions.set([this.xOf(c), this.yOf(e), this.zOf(r)], i * 3);
        if (e > 0) this.landTop = Math.max(this.landTop, LAND_SHARE_PER_M * e * H);
      }
    }
    const floor = this.floor();
    this.floorMesh = floor;
    this.floorColors = floor.geometry.getAttribute('color') as THREE.BufferAttribute;
    this.water = floor.geometry.getAttribute('aWater') as THREE.BufferAttribute;
    this.caustic = floor.geometry.getAttribute('aCaustic') as THREE.BufferAttribute;
    this.causticDrift = floor.userData.causticDrift as CausticUniforms['uCausticDrift'];
    this.backdrop = this.backdropOf();
    const surface = this.surfaceOf();
    this.surface = surface.userData.uniforms as { uTime: { value: number } };
    this.ruler = this.rulerOf();
    this.cut = this.cutFaces();
    this.group.add(floor, this.cut, this.backdrop.mesh, this.glass(), surface, this.ruler, this.key);
    exhibit.bands.forEach((band, k) => {
      const name = label(bandNames[k] ?? '', 12, '#ffe9a8');
      name.center.set(0.5, 0);
      name.position.set(this.xOf(Math.round(exhibit.across(0) * (COLS - 1))), TOP + 0.15, this.zOf(band.row));
      this.group.add(name);
    });
    // The reef's fish, bound to its columns, swimming over the floor as drawn; each school's commute counted in the
    // display grid's steps, as the map's in its kilometer columns (a step about 20 m of real reef across the shore,
    // and between the bands' rows no real distance: drawn, as the exhibit is, not to scale; M94's review).
    const reefOf = new Map(exhibit.reef.columns.map((col, j) => [col, exhibit.reef.island[j] as number]));
    const place = (column: number, out: THREE.Vector3): THREE.Vector3 => out.fromArray(this.positions, column * 3);
    // The tank's glass a shore to them, its floor read as above the sea past it, so a fish whose place lies beyond is
    // drawn in as at a coast (M99's test-breaker: 4.3% of fish-frames past the front or back glass on the first day,
    // by up to 1.35 units, the floor beyond read as the edge's water).
    const water = (x: number, z: number): number => (Math.abs(x) > W / 2 || Math.abs(z) > D / 2 ? TOP + H : this.floorAt(x, z));
    this.fish = new FishSchools(FISH_KINDS, COLS, 1, reefOf, place, water, TOP, EXHIBIT_SPRITES.fishPerSprite, EXHIBIT_SPRITES.scale, true);
    this.group.add(this.fish.group);
    // The humpbacks (M99), in the water over the floor as drawn, by the floor's real depths.
    this.whales = new ExhibitWhales({
      x0: -W / 2, x1: W / 2, z0: -D / 2 + 0.5, z1: D / 2 - 0.5,
      floorY: (x, z) => this.floorAt(x, z),
      depthY: (m) => TOP - exhibit.depthShare(m) * H,
      depthOf: (y) => (y >= TOP ? 0 : exhibit.depthAt((TOP - y) / H)),
    });
    this.group.add(this.whales.group);
    // The honu (M104), on their islands' coasts: each island's band of rows, its middle half, away from the glass.
    const coastZ = (island: number, u: number): number | null => {
      const band = exhibit.bands.find((b) => b.island === island);
      return band ? Math.max(-D / 2 + 0.5, Math.min(D / 2 - 0.5, this.zOf(band.row + (u - 0.5) * (ROWS / exhibit.bands.length) * 0.5))) : null;
    };
    this.turtles = new ExhibitTurtles({
      x0: -W / 2, x1: W / 2, z0: -D / 2 + 0.5, z1: D / 2 - 0.5, top: TOP,
      floorY: (x, z) => this.floorAt(x, z),
      depthOf: (y) => (y >= TOP ? 0 : exhibit.depthAt((TOP - y) / H)),
      zOf: coastZ,
    }, 10 * UNITS_PER_MM * EXHIBIT_SPRITES.scale);
    this.group.add(this.turtles.group);
    // The monk seals (M145), on their islands' coasts as the honu, at the depths they forage at.
    this.seals = new ExhibitSeals({
      x0: -W / 2, x1: W / 2, z0: -D / 2 + 0.5, z1: D / 2 - 0.5, top: TOP,
      floorY: (x, z) => this.floorAt(x, z),
      depthOf: (y) => (y >= TOP ? 0 : exhibit.depthAt((TOP - y) / H)),
      zOf: coastZ,
    });
    this.group.add(this.seals.group);
    this.group.add(this.marks.group);
    this.plankton = this.planktonOf();
    this.group.add(this.plankton);
    this.habitats = exhibit.habitats;
    // The animals fade into the water as the floor behind them does (M105).
    for (const fogged of [this.fish, this.turtles, this.seals, this.whales]) this.setFog(fogged.fogUniforms());
    this.corners = [-W / 2, W / 2].flatMap((x) => [-D / 2, D / 2].flatMap((z) => [[x, BOTTOM, z], [x, TOP + this.landTop, z]] as const));
    this.radius = Math.hypot(W / 2, D / 2, H / 2 + this.landTop);
    this.scaleNote = `as an exhibit of its waters, stretched: depth by the brief's stops, distance from the coast with it, so its mean floor's slopes are drawn about ${Math.round(exhibit.exaggeration)} times as steep as they are (less in the deep) and each island's steeper or less as its own depths go; each island's real floor to 300 m, then the real descent to the deepest water`;
  }

  /**
   * A habitat's shot (M105, `HABITAT_SHOTS`): its target across the habitat's box and behind the tank's middle, its
   * share of the way up from the floor there to the habitat's top; null for a habitat without one, and on a site with
   * no reef, whose tank the shots were not composed on (the Cape Peninsula's keeps its boxes).
   */
  private shotOf(id: Habitat['id'] | 'overview' | 'all'): Frame | null {
    const shot = (HABITAT_SHOTS as Partial<Record<string, (typeof HABITAT_SHOTS)[keyof typeof HABITAT_SHOTS]>>)[id];
    const box = this.habitats.find((h) => h.id === id);
    if (!shot || !box || this.exhibit.reef.columns.length === 0) return null;
    const [x0, x1] = box.across.map((u) => -W / 2 + u * W) as [number, number];
    const [x, z] = [x0 + shot.across * (x1 - x0), -shot.back * D];
    const floor = this.floorAt(x, z);
    const y = floor + shot.aboveFloor * (Math.min(TOP, TOP - box.down[0] * H) - floor);
    const r = shot.half;
    const points = [x - r, x + r].flatMap((px) => [y - r, y + r].map((py) => [px, py, z] as const));
    return { target: [x, y, z], points, direction: shot.direction, half: r };
  }

  /**
   * What a camera's view is made of (M105, the redesign v2's composition, measured, not guessed): rays cast through an
   * even grid of the view, `cols` by `rows`, each marched through the tank against its floor (`floorAt`, the heights
   * its mesh is built from) by `MARCH_UNITS`: the cutaway's rock where it enters the tank under the floor's edge; the
   * floor where it first goes under it (land where that stands above the sea); the reef's outcrops where it first
   * passes within half an outcrop's width of one's middle; else water where it passes through the water, and empty
   * where it does not (above the surface, past the tank). Shares of the view.
   */
  shotComposition(camera: THREE.Camera, cols = 30, rows = 50): { floor: number; coral: number; land: number; rock: number; water: number; empty: number } {
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const out = { floor: 0, coral: 0, land: 0, rock: 0, water: 0, empty: 0 };
    const tank = new THREE.Box3(new THREE.Vector3(-W / 2, BOTTOM, -D / 2), new THREE.Vector3(W / 2, TOP + this.landTop + 0.01, D / 2));
    const enter = new THREE.Vector3();
    const at = new THREE.Vector3();
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        ndc.set(((i + 0.5) / cols) * 2 - 1, ((j + 0.5) / rows) * 2 - 1);
        raycaster.setFromCamera(ndc, camera);
        const ray = raycaster.ray;
        if (!ray.intersectBox(tank, enter)) {
          out.empty++;
          continue;
        }
        const t0 = enter.distanceTo(ray.origin);
        // Inside the tank already where the camera is in it.
        const start = tank.containsPoint(ray.origin) ? 0 : t0;
        if (start > 0 && enter.y < this.floorAt(enter.x, enter.z) - 1e-6) {
          out.rock++;
          continue;
        }
        let what: keyof typeof out | null = null;
        let wet = false;
        for (let t = start; ; t += MARCH_UNITS) {
          ray.at(t, at);
          if (!tank.containsPoint(at)) break;
          if (at.y < TOP) wet = true;
          if (at.y < this.floorAt(at.x, at.z)) {
            what = at.y >= TOP ? 'land' : 'floor';
            break;
          }
          if (this.outcrops?.solidAt(at)) {
            what = 'coral';
            break;
          }
        }
        out[what ?? (wet ? 'water' : 'empty')]++;
      }
    }
    const n = cols * rows;
    return { floor: out.floor / n, coral: out.coral / n, land: out.land / n, rock: out.rock / n, water: out.water / n, empty: out.empty / n };
  }

  /** Scene x of a grid column, z of a grid row (row 0 at the back), y of a real elevation. */
  private xOf(c: number): number {
    return -W / 2 + (c / (COLS - 1)) * W;
  }

  private zOf(r: number): number {
    return -D / 2 + (r / (ROWS - 1)) * D;
  }

  private yOf(e: number): number {
    return e >= 0 ? TOP + LAND_SHARE_PER_M * e * H : TOP - this.exhibit.depthShare(-e) * H;
  }

  /** The floor's height under a point, scene units, between its vertices. */
  floorAt(x: number, z: number): number {
    const fc = Math.min(COLS - 1.000001, Math.max(0, ((x + W / 2) / W) * (COLS - 1)));
    const fr = Math.min(ROWS - 1.000001, Math.max(0, ((z + D / 2) / D) * (ROWS - 1)));
    const [c, r] = [Math.floor(fc), Math.floor(fr)];
    const [s, t] = [fc - c, fr - r];
    const y = (rr: number, cc: number): number => this.positions[(rr * COLS + cc) * 3 + 1] as number;
    return (y(r, c) * (1 - s) + y(r, c + 1) * s) * (1 - t) + (y(r + 1, c) * (1 - s) + y(r + 1, c + 1) * s) * t;
  }

  /**
   * A habitat's frame, or the whole tank's: its box across the tank's depth, land and all for the overview, a panorama
   * (`WHOLE_TANK`), and all of it at once for Fit All (`all`).
   */
  frameOf(id: Habitat['id'] | 'overview' | 'all'): Frame {
    const shot = this.shotOf(id);
    if (shot) return shot;
    const habitat = this.habitats.find((h) => h.id === id);
    const [x0, x1] = habitat ? habitat.across.map((u) => -W / 2 + u * W) : [-W / 2, W / 2];
    const [y1, y0] = habitat ? habitat.down.map((d) => TOP - d * H) : [TOP + this.landTop, BOTTOM];
    const points = [x0, x1].flatMap((x) => [y0, y1].flatMap((y) => [-D / 2, D / 2].map((z) => [x as number, y as number, z] as const)));
    const target: [number, number, number] = [((x0 as number) + (x1 as number)) / 2, ((y0 as number) + (y1 as number)) / 2, 0];
    if (id !== 'overview') return { target, points };
    // The panorama by the water's height, the surface to the floor of the tank: the land stands only at the coast's
    // end, off a narrow screen until the player pans to it (MEASURED on a phone, 47% of the view empty where its
    // height with the land's was 48%, and all of it at once 75%).
    const water = points.map(([x, y, z]) => [x, Math.min(y, TOP), z] as const);
    return { target: [target[0], (TOP + BOTTOM) / 2, 0], points: water, tall: WHOLE_TANK.mostCloser };
  }

  /** The floor: its vertices' data colors and the attributes its paint reads. */
  private floor(): THREE.Mesh {
    const n = COLS * ROWS;
    const colors = new Float32Array(n * 3);
    const land = new Float32Array(n);
    const reefShare = new Float32Array(n);
    const slope = new Float32Array(n);
    const depth = new Float32Array(n);
    const sand = new THREE.Color(0xd9c697);
    const ground = new THREE.Color(0x8a9a5a);
    for (let i = 0; i < n; i++) {
      const e = this.exhibit.elevationM[i] as number;
      land[i] = e >= 0 ? 1 : 0;
      depth[i] = Math.max(0, -e);
      slope[i] = this.exhibit.slopeDeg[i] as number;
      (e >= 0 ? ground : sand).toArray(colors, i * 3);
    }
    for (const col of this.exhibit.reef.columns) {
      reefShare[col] = 1;
      RUBBLE.toArray(colors, col * 3);
    }
    const index: number[] = [];
    for (let r = 0; r < ROWS - 1; r++) {
      for (let c = 0; c < COLS - 1; c++) {
        const a = r * COLS + c;
        index.push(a, a + COLS, a + 1, a + 1, a + COLS, a + COLS + 1);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aSkinLand', new THREE.BufferAttribute(land, 1));
    geometry.setAttribute('aSkinReef', new THREE.BufferAttribute(reefShare, 1));
    geometry.setAttribute('aSlope', new THREE.BufferAttribute(slope, 1));
    geometry.setAttribute('aDepth', new THREE.BufferAttribute(depth, 1));
    // The day's light at each vertex, written each day (`setDay`); the deep's least until the first.
    geometry.setAttribute('aWater', new THREE.BufferAttribute(new Float32Array(n * 3).fill(VISIBLE), 3));
    geometry.setAttribute('aCaustic', new THREE.BufferAttribute(new Float32Array(n), 1));
    geometry.setIndex(index);
    geometry.computeVertexNormals();
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });
    const linear = (hex: number): THREE.Color => new THREE.Color(hex);
    const paint: ExhibitPaint = {
      sand: PAINT_HEX.sand.map(linear),
      silt: PAINT_HEX.silt.map(linear),
      basalt: PAINT_HEX.basalt.map(linear),
      coralline: PAINT_HEX.coralline.map(linear),
      rockFromDeg: ROCK_DEG[0],
      rockToDeg: ROCK_DEG[1],
      siltFromM: SILT_M[0],
      siltToM: SILT_M[1],
    };
    const skin = attachSkin(material, { texel: EXHIBIT_TEXEL, caustics: true, fog: true, paint });
    this.setFog(skin);
    if (skin.uCausticContrast) skin.uCausticContrast.value = CAUSTIC_CONTRAST;
    if (skin.uCausticSurface) skin.uCausticSurface.value = TOP;
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.causticDrift = skin.uCausticDrift;
    return mesh;
  }

  /** Fogs a surface seen through the water: bluer near the top, darker and thicker toward the deep. */
  private setFog(skin: Partial<FogUniforms>): void {
    if (!skin.uFogBoxMin || !skin.uFogBoxMax || !skin.uFogDensity || !skin.uFogColor || !skin.uFogDeep || !skin.uFogDeepAt || !skin.uFogDeepDensity) throw new Error('a fogged surface without its fog');
    this.fogged.push(skin);
    skin.uFogBoxMin.value.set(-W / 2, BOTTOM, -D / 2);
    skin.uFogBoxMax.value.set(W / 2, TOP, D / 2);
    skin.uFogDensity.value = FOG_DENSITY;
    skin.uFogColor.value.setHex(FOG_TOP);
    skin.uFogDeep.value.setHex(FOG_DEEP);
    skin.uFogDeepAt.value.set(TOP - this.exhibit.depthShare(30) * H, TOP - this.exhibit.depthShare(1000) * H);
    skin.uFogDeepDensity.value = FOG_DEEP_DENSITY;
  }

  /**
   * The cross-section where the glass cuts the floor, at the front and the two ends, down to the tank's floor:
   * basalt in pixel bands, darker with depth (JUDGED: the islands are stacked basalt; a section, not a habitat).
   */
  private cutFaces(): THREE.Mesh {
    const positions: number[] = [];
    const shade: number[] = [];
    const edge = (cells: Array<[number, number]>): void => {
      for (let k = 0; k < cells.length - 1; k++) {
        const [r0, c0] = cells[k] as [number, number];
        const [r1, c1] = cells[k + 1] as [number, number];
        const a = (r0 * COLS + c0) * 3;
        const b = (r1 * COLS + c1) * 3;
        const [x0, y0, z0] = [this.positions[a] as number, this.positions[a + 1] as number, this.positions[a + 2] as number];
        const [x1, y1, z1] = [this.positions[b] as number, this.positions[b + 1] as number, this.positions[b + 2] as number];
        positions.push(x0, y0, z0, x0, BOTTOM, z0, x1, y1, z1, x1, y1, z1, x0, BOTTOM, z0, x1, BOTTOM, z1);
      }
    };
    edge(Array.from({ length: COLS }, (_, c) => [ROWS - 1, c]));
    edge(Array.from({ length: ROWS }, (_, r) => [r, 0]));
    edge(Array.from({ length: ROWS }, (_, r) => [r, COLS - 1]));
    for (let i = 0; i < positions.length; i += 3) shade.push(CUT_SHADE[0] + (CUT_SHADE[1] - CUT_SHADE[0]) * Math.max(0, Math.min(1, ((positions[i + 1] as number) - BOTTOM) / H)));
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('aShade', new THREE.Float32BufferAttribute(shade, 1));
    const material = new THREE.MeshBasicMaterial({ color: 0x3c4756, side: THREE.DoubleSide });
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uTexel = { value: CUT_TEXEL };
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float aShade;\nvarying float vShade;\nvarying vec3 vAt;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvShade = aShade;\nvAt = transformed;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
        uniform float uTexel;
        varying float vShade;
        varying vec3 vAt;
        // A hash of a texel, 0 to 1, in integer arithmetic (a sine's hash streaked at these coordinates).
        float cutHash(vec2 p) {
          uvec2 v = uvec2(ivec2(p) + 4096) * uvec2(1664525u, 1013904223u);
          v.x += v.y * 1664525u; v.y += v.x * 1664525u;
          v ^= v >> 16u;
          v.x += v.y * 1664525u;
          return float(v.x) / 4294967295.0;
        }
        // Smooth value noise over the texels, 0 to 1, its lattice a cell of ${CUT_BROAD.join(' by ')} texels.
        float cutNoise(vec2 t) {
          vec2 p = t / vec2(${CUT_BROAD[0].toFixed(1)}, ${CUT_BROAD[1].toFixed(1)});
          vec2 i = floor(p);
          vec2 f = p - i;
          f = f * f * (3.0 - 2.0 * f);
          return mix(mix(cutHash(i + 9000.0), cutHash(i + vec2(9001.0, 9000.0)), f.x), mix(cutHash(i + vec2(9000.0, 9001.0)), cutHash(i + 9001.0), f.x), f.y);
        }`)
        .replace('#include <color_fragment>', `#include <color_fragment>
        // Flows two texels thick, each its own shade, a few joints dark along them, crisp as pixel art.
        vec2 cutTexel = floor(vec2(vAt.x + vAt.z, vAt.y) / uTexel);
        float cutFlow = cutHash(vec2(0.0, floor(cutTexel.y / 2.0)));
        float cutJoint = cutHash(cutTexel);
        // Broad drifts of lighter, bluer basalt (M105, the redesign v2's "large-scale blue-gray variation", "no uniform
        // dark walls"), and here and there a flow's top lit as a ledge (its "occasional ledges").
        float cutBroad = cutNoise(cutTexel);
        float cutLedge = mod(cutTexel.y, 2.0) == 1.0 && cutHash(vec2(floor(cutTexel.x / ${CUT_LEDGE.texels.toFixed(1)}), floor(cutTexel.y / 2.0)) + 5000.0) > ${(1 - CUT_LEDGE.share).toFixed(2)} ? ${CUT_LEDGE.lift.toFixed(2)} : 1.0;
        diffuseColor.rgb *= vShade * (0.86 + 0.14 * cutFlow) * (cutJoint > 0.96 ? 0.82 : 1.0) * cutLedge
          * mix(vec3(${CUT_DRIFT.dark.map((v) => v.toFixed(2)).join(', ')}), vec3(${CUT_DRIFT.light.map((v) => v.toFixed(2)).join(', ')}), cutBroad);`);
    };
    material.customProgramCacheKey = () => 'exhibit-cut';
    return new THREE.Mesh(geometry, material);
  }

  /** The water's color behind the tank, by depth, in rows; lit each day where the light reaches (`setDay`). */
  private backdropOf(): { mesh: THREE.Mesh; colors: THREE.BufferAttribute; base: Float32Array; depthM: Float32Array } {
    const rows = 240;
    const geometry = new THREE.PlaneGeometry(W, H, 1, rows);
    const position = geometry.getAttribute('position');
    const base = new Float32Array(position.count * 3);
    const depthM = new Float32Array(position.count);
    const c = new THREE.Color();
    for (let i = 0; i < position.count; i++) {
      const share = (TOP - position.getY(i)) / H;
      depthM[i] = this.exhibit.depthAt(share);
      rampColor(BACKDROP, depthM[i] as number, c).toArray(base, i * 3);
    }
    const colors = new THREE.BufferAttribute(base.slice(), 3);
    geometry.setAttribute('color', colors);
    const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ vertexColors: true }));
    mesh.position.set(0, 0, -D / 2 - 0.02);
    // Not under the pointer: a zoom aims at the floor, not the water behind it.
    mesh.raycast = () => undefined;
    return { mesh, colors, base, depthM };
  }

  /** The glass: the front nearly clear, the right end a faint tint, and its edges (the brief: "unobtrusive"). */
  private glass(): THREE.Group {
    const group = new THREE.Group();
    const pane = (width: number, opacity: number): THREE.Mesh => {
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, H), new THREE.MeshBasicMaterial({ color: 0x4f9fcc, transparent: true, opacity, depthWrite: false }));
      mesh.renderOrder = 1;
      mesh.raycast = () => undefined;
      return mesh;
    };
    const front = pane(W, FRONT_OPACITY);
    front.position.set(0, 0, D / 2);
    const side = pane(D, SIDE_OPACITY);
    side.rotation.y = Math.PI / 2;
    side.position.set(W / 2, 0, 0);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(W, H, D)), new THREE.LineBasicMaterial({ color: 0xbfe6ff, transparent: true, opacity: 0.45 }));
    group.add(front, side, edges);
    return group;
  }

  /**
   * The water's surface: a faint pane with a slow, soft shimmer (the brief: "subtle surface motion"), no pattern of
   * tiles; from under the water, the water's color mirrored (`UNDERSIDE_OPACITY`).
   */
  private surfaceOf(): THREE.Mesh {
    const uniforms = { uTime: { value: 0 } };
    const shimmer = (material: THREE.MeshBasicMaterial, key: string): THREE.MeshBasicMaterial => {
      material.onBeforeCompile = (shader) => {
        Object.assign(shader.uniforms, uniforms);
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec2 vSurface;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSurface = position.xy;');
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform float uTime;\nvarying vec2 vSurface;')
          .replace('#include <color_fragment>', `#include <color_fragment>
          // Two soft swells crossing, slowly: at most a third more or less opaque.
          float swell = sin(vSurface.x * 0.9 + uTime * 0.35) * sin(vSurface.y * 1.3 - uTime * 0.27) + 0.5 * sin((vSurface.x + vSurface.y) * 0.6 + uTime * 0.21);
          diffuseColor.a *= 1.0 + 0.22 * swell;`);
      };
      material.customProgramCacheKey = () => key;
      return material;
    };
    const plane = new THREE.PlaneGeometry(W, D);
    const mesh = new THREE.Mesh(plane, shimmer(new THREE.MeshBasicMaterial({ color: 0x9fdcff, transparent: true, opacity: SURFACE_OPACITY, depthWrite: false }), 'exhibit-surface'));
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = TOP - 0.01;
    mesh.renderOrder = 2;
    mesh.raycast = () => undefined;
    mesh.userData.uniforms = uniforms;
    // Its face down, the underside, seen only from under the water; colored by `setLight`.
    this.underside = new THREE.Mesh(plane, shimmer(new THREE.MeshBasicMaterial({ color: BACKDROP[0]?.[1], transparent: true, opacity: UNDERSIDE_OPACITY, side: THREE.BackSide, depthWrite: false }), 'exhibit-underside'));
    this.underside.renderOrder = 2;
    this.underside.raycast = () => undefined;
    mesh.add(this.underside);
    return mesh;
  }

  /**
   * The true-depth ruler beside a frame, so it is always in view (the brief, section 3.2: "a true-depth ruler is
   * always shown"; M94's review: at the tank's right end, three of the five habitats had none): at the whole
   * tank's right front corner, or inside a habitat's frame, a tenth of its width from its right edge; by the front
   * glass, or for a habitat's shot on its target's plane, as its camera may stand inside the glass (M105's review: in
   * four of the five shots on a phone the ruler stood behind it).
   */
  rulerAt(frame: Frame): void {
    const xs = frame.points.map((p) => p[0]);
    const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
    const whole = x0 <= -W / 2 && x1 >= W / 2;
    this.ruler.position.x = whole ? 0 : x1 - 0.1 * (x1 - x0) - (W / 2 + 0.2);
    this.ruler.position.z = frame.half !== undefined ? frame.target[2] - (D / 2 + 0.2) : 0;
    this.ruler.updateMatrixWorld(true);
    // Its labels sorted again where they now stand (`fitLabels`).
    this.rulerFor.fill(Number.NaN);
  }

  /**
   * The ruler kept in view (M104, the brief's section 3.2: "a true-depth ruler is always shown"): where the player has
   * turned or zoomed it off the part of the screen the panels leave, `left` to `right` in the screen's -1 to 1, it is
   * moved along its plane (`rulerAt`'s) to that part's nearer edge, a tenth in; never past the tank's ends. Where it is
   * in view it stays.
   */
  keepRulerInView(camera: THREE.Camera, left = -1, right = 1): void {
    const [baseX, z] = [W / 2 + 0.2, D / 2 + 0.2 + this.ruler.position.z];
    const margin = 0.1 * (right - left);
    // Where the ruler crosses the screen's middle row: its line's ends projected, and the screen x between them there.
    const top = new THREE.Vector3(baseX + this.ruler.position.x, TOP, z).project(camera);
    const bottom = new THREE.Vector3(baseX + this.ruler.position.x, BOTTOM, z).project(camera);
    const atX = Math.abs(top.y - bottom.y) > 1e-9 ? top.x + ((bottom.x - top.x) * (0 - top.y)) / (bottom.y - top.y) : top.x;
    if (atX >= left + margin / 2 && atX <= right - margin / 2) return;
    // Where the screen's middle row meets the front glass, at the nearer edge of the part left.
    const ndc = atX < left + margin / 2 ? left + margin : right - margin;
    const near = new THREE.Vector3(ndc, 0, -1).unproject(camera);
    const far = new THREE.Vector3(ndc, 0, 1).unproject(camera);
    const t = (z - near.z) / (far.z - near.z);
    if (!Number.isFinite(t)) return;
    const x = near.x + (far.x - near.x) * t;
    this.ruler.position.x = Math.min(0, Math.max(-W - 0.4, x - baseX));
    this.ruler.updateMatrixWorld(true);
    this.rulerFor.fill(Number.NaN);
  }

  /** The true-depth ruler at the right front corner (`rulerAt` moves it): real meters at their stretched places, and the layers named. */
  private rulerOf(): THREE.Group {
    const group = new THREE.Group();
    const [x, z] = [W / 2 + 0.2, D / 2 + 0.2];
    const yOf = (m: number): number => TOP - this.exhibit.depthShare(m) * H;
    const points: number[] = [x, TOP, z, x, BOTTOM, z];
    const deepest = this.exhibit.deepestM;
    const ticks = [0, 10, 20, 30, 50, 100, 200, 500, 1000, 2000, 3000, 4000].filter((m) => m < deepest);
    for (const m of ticks) {
      const y = yOf(m);
      points.push(x, y, z, x + 0.25, y, z);
      const tick = label(`${m.toLocaleString('en-US')} m`, 11);
      tick.center.set(0, 0.5);
      tick.position.set(x + 0.35, y, z);
      tick.userData.depthM = m;
      tick.userData.keep = m === 0 || m === 30 || m === 200 || m === 1000;
      group.add(tick);
    }
    // The stretch said on the aquarium itself, over the ruler (M104, the brief's section 3.2: the exaggeration labeled).
    const stretch = label(`depth stretched: slopes ${Math.round(this.exhibit.exaggeration)}× as steep`, 11, '#9fd6ff');
    stretch.center.set(0.5, 0);
    stretch.position.set(x, TOP + 0.35, z);
    stretch.name = 'stretch';
    group.add(stretch);
    const floor = label(`${Math.round(deepest).toLocaleString('en-US')} m`, 11, '#9fd6ff');
    floor.center.set(0, 0.5);
    floor.position.set(x + 0.35, BOTTOM, z);
    floor.userData.depthM = deepest;
    floor.userData.keep = true;
    group.add(floor);
    for (const zone of DEPTH_ZONES) {
      if (zone.topM >= deepest) continue;
      const top = yOf(zone.topM);
      const bottom = yOf(Math.min(zone.bottomM, deepest));
      if (top - bottom < 0.8) continue;
      const name = label(zone.zone, 11, '#a9cbe0');
      name.center.set(1, 0.5);
      name.position.set(x - 0.15, (top + bottom) / 2, z);
      group.add(name);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    group.add(new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: 0xdff3ff })));
    return group;
  }

  /** The specks' points: every one's place set by `setPlankton`, drawn a few pixels across whatever the zoom. */
  private planktonOf(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(PLANKTON.most * 3), 3));
    geometry.setDrawRange(0, 0);
    const material = new THREE.PointsMaterial({ color: 0xd8f0ff, size: PLANKTON.px, sizeAttenuation: false, transparent: true, opacity: PLANKTON.opacity, depthWrite: false });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.renderOrder = 5;
    return points;
  }

  /**
   * The zooplankton of a day (`PLANKTON`): `zooMmolNPerM3` through a mixed layer `mixedLayerM` deep, as specks in
   * clumps through it, each over water deeper than its own depth; laid again only when either changes.
   */
  setPlankton(zooMmolNPerM3: number, mixedLayerM: number): void {
    const n = Math.min(PLANKTON.most, Math.round(PLANKTON.specksPerMmolNM2 * zooMmolNPerM3 * mixedLayerM));
    const key = `${n}:${Math.round(mixedLayerM)}`;
    if (key === this.planktonFor) return;
    this.planktonFor = key;
    const position = this.plankton.geometry.getAttribute('position') as THREE.BufferAttribute;
    let k = 0;
    const [top, foot] = [TOP, TOP - this.exhibit.depthShare(mixedLayerM) * H];
    for (let i = 0; k < n && i < n * 4; i++) {
      // Each speck's clump by its number, each clump's middle by its own number's hash, and the speck about it by its
      // own, so the same specks stand where they stood when the count moves (M105: in clumps, not spread evenly).
      const c = Math.floor(i / PLANKTON.perClump);
      const u = COAST_SHARE + hash3(c, 1, 97) * (1 - COAST_SHARE);
      const [a, r] = [hash3(i, 4, 97) * 2 * Math.PI, (PLANKTON.clumpUnits / 2) * Math.sqrt(hash3(i, 5, 97))];
      const x = -W / 2 + u * W + r * Math.cos(a);
      const z = Math.max(-D / 2, Math.min(D / 2, -D / 2 + hash3(c, 3, 97) * D + r * Math.sin(a)));
      const middle = TOP - this.exhibit.depthShare(hash3(c, 2, 97) * mixedLayerM) * H;
      const y = Math.max(foot, Math.min(top, middle + (hash3(i, 6, 97) - 0.5) * (PLANKTON.clumpUnits / 2)));
      if (Math.abs(x) > W / 2 || this.floorAt(x, z) >= y) continue;
      position.setXYZ(k++, x, y, z);
    }
    position.needsUpdate = true;
    this.plankton.geometry.setDrawRange(0, k);
  }

  /** The specks drawn now, for the tests: how many, and their heights. */
  planktonDrawn(): number[] {
    const position = this.plankton.geometry.getAttribute('position') as THREE.BufferAttribute;
    return Array.from({ length: this.plankton.geometry.drawRange.count }, (_, i) => position.getY(i));
  }

  /** The school of the fish drawn nearest a point on the screen, within `withinPx` pixels, by its seed; -1 for none (`FishSchools.pick`). */
  pickSchool(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return this.fish.pick(camera, x, y, widthPx, heightPx, withinPx);
  }

  /** A school as the wildlife card tells it, by its seed, with its fish's mean real depth, m (M97); null for none. */
  schoolAt(seed: number): (NonNullable<ReturnType<FishSchools['schoolAt']>> & { species: (typeof FISH_KINDS)[number]['species']; depthM: number; spritesOfKind: number; kindPerSprite: number; level: number }) | null {
    const s = this.fish.schoolAt(seed);
    if (!s) return null;
    const kind = FISH_KINDS[s.kind] as (typeof FISH_KINDS)[number];
    return { ...s, species: kind.species, depthM: this.exhibit.depthAt((TOP - s.middle.y) / H), spritesOfKind: this.fish.spritesOf(s.kind), kindPerSprite: kind.perSprite ?? EXHIBIT_SPRITES.fishPerSprite, level: this.fish.level() };
  }

  /** A day's light at noon (`setLight`'s, from the noon sun). */
  setDay(day: DaySun): void {
    const direction = day.meridianDeg === null ? sunDirection(90) : sunDirection(day.meridianDeg);
    this.setLight({ direction, elevationDeg: day.meridianDeg === null ? -90 : 90 - Math.abs(90 - day.meridianDeg), beam: day.beam, level: day.beam, sky: day.meridianDeg === null ? 0 : 1, kdPar: day.kdPar });
  }

  /**
   * The light at an hour (M95, the observation clock's): the key light from where the sun stands then, at its
   * beam's strength; each part of the floor by the light that reaches its real depth (on a log scale, never under
   * `VISIBLE`), its reds fading first; the caustics by that light; the water behind glowing where it reaches,
   * and dimmed by night to `NIGHT_BACKDROP` of its color (a rendering choice: the night seen at all); the fish by
   * the light at `FISH_LIGHT_M` against a full sun's there, so they keep their own colors by day (M96).
   */
  setLight(sun: HourSun): void {
    // The sun's light, or the moon's by its phase and height when that is more (M104), drawn far brighter than it is.
    const moon = sun.moon?.level ?? 0;
    const strength = Math.max(Math.min(1, sun.level), NIGHT_LIGHT.moonDrawn * moon);
    const seen = (share: number): number => VISIBLE + (1 - VISIBLE) * Math.min(1, Math.max(0, (Math.log10(Math.max(share, 1e-9)) + 4) / 4));
    const fish = seen(strength * lightShare(sun.kdPar, FISH_LIGHT_M)) / seen(lightShare(sun.kdPar, FISH_LIGHT_M));
    // Warmer as the sun nears the horizon: the key light's color, and the animals and the outcrops tinted with it.
    const warm = sun.beam > 0 ? Math.min(1, Math.max(0, 1 - sun.elevationDeg / WARM.belowDeg)) : 0;
    const tint: [number, number, number] = [fish, fish * (1 - WARM.tint[0] * warm), fish * (1 - WARM.tint[1] * warm)];
    this.fish.setLight(...tint);
    this.whales.setLight(...tint);
    this.turtles.setLight(...tint);
    this.seals.setLight(...tint);
    this.outcrops?.setLight(...tint);
    this.lastTint = tint;
    (this.plankton.material as THREE.PointsMaterial).opacity = PLANKTON.opacity * fish;
    if (sun.beam > 0 || !sun.moon || moon <= 0) {
      this.key.intensity = KEY_AT_FULL_SUN * sun.beam;
      this.key.position.set(...sun.direction);
      this.key.color.setHex(WARM.sunColor).lerp(new THREE.Color(WARM.color), warm);
    } else {
      this.key.intensity = KEY_AT_FULL_SUN * NIGHT_LIGHT.moonKey * moon;
      this.key.position.set(...sun.moon.direction);
      this.key.color.setHex(NIGHT_LIGHT.moonColor);
    }
    const water = this.water.array as Float32Array;
    const caustic = this.caustic.array as Float32Array;
    for (let i = 0; i < COLS * ROWS; i++) {
      const e = this.exhibit.elevationM[i] as number;
      if (e >= 0) {
        water.fill(VISIBLE + (1 - VISIBLE) * strength, i * 3, i * 3 + 3);
        caustic[i] = 0;
        continue;
      }
      const share = strength * lightShare(sun.kdPar, -e);
      const level = seen(share);
      const z = Math.min(-e, FADE_TO_M);
      water.set([level * Math.exp(-FADE_PER_M[0] * z), level * Math.exp(-FADE_PER_M[1] * z), level * Math.exp(-FADE_PER_M[2] * z)], i * 3);
      caustic[i] = share;
    }
    this.water.needsUpdate = true;
    this.caustic.needsUpdate = true;
    const lit = new THREE.Color(LIT);
    const c = new THREE.Color();
    const { colors, base, depthM } = this.backdrop;
    const dim = NIGHT_BACKDROP + (1 - NIGHT_BACKDROP) * sun.sky;
    const green = new THREE.Color(WATER_STATE.green);
    for (let i = 0; i < depthM.length; i++) {
      const g = glowAt(sun.kdPar, depthM[i] as number) * strength;
      c.fromArray(base, i * 3).lerp(green, this.greenness).multiplyScalar(dim).lerp(lit, g);
      colors.setXYZ(i, c.r, c.g, c.b);
      // The underside the water's color at the top, the backdrop's first row.
      if (i === 0) this.underside.material.color.copy(c);
    }
    colors.needsUpdate = true;
  }

  /**
   * The water by its measured state on a day (M104): its fog by its clarity, its color greener with its
   * chlorophyll (`WATER_STATE`); the backdrop's green laid on at the next `setLight`.
   */
  setWater(kdPar: number, chlorophyllMgM3: number): void {
    const density = FOG_DENSITY * (kdPar > 0 ? kdPar / WATER_STATE.kdRef : 1);
    const top = new THREE.Color(FOG_TOP);
    const span = Math.log10(WATER_STATE.chlGreen / WATER_STATE.chlClear);
    this.greenness = WATER_STATE.mostGreen * Math.min(1, Math.max(0, Math.log10(Math.max(chlorophyllMgM3, 1e-6) / WATER_STATE.chlClear) / span));
    top.lerp(new THREE.Color(WATER_STATE.green), this.greenness);
    for (const skin of this.fogged) {
      if (skin.uFogDensity) skin.uFogDensity.value = density;
      skin.uFogColor?.value.copy(top);
    }
  }

  /** The water's fog density, its color's green share and the fog's color, as `setWater` last set them, for the tests. */
  waterState(): { fogDensity: number; greenness: number; fogColor: string } {
    return { fogDensity: this.fogged[0]?.uFogDensity?.value ?? FOG_DENSITY, greenness: this.greenness, fogColor: this.fogged[0]?.uFogColor?.value.getHexString() ?? '' };
  }

  /** The reef's pavement tinted for the day's coral: toward live coral by its cover, toward white as it bleaches. */
  setReef(columnCoverPercent: readonly number[], columnBleachedPercent: readonly number[], columnKilledPercent: readonly number[] = []): void {
    const colors = this.floorColors.array as Float32Array;
    const c = new THREE.Color();
    this.exhibit.reef.columns.forEach((col, j) => {
      const live = Math.min(1, (columnCoverPercent[j] ?? 0) / CORAL_RATES.ceilingPercent);
      c.copy(RUBBLE).lerp(LIVE_CORAL, MOST_TINT * live).lerp(BLEACHED_CORAL, MOST_TINT * Math.min(1, (columnBleachedPercent[j] ?? 0) / 100));
      c.toArray(colors, col * 3);
    });
    this.floorColors.needsUpdate = true;
    this.lastCover = { cover: columnCoverPercent, bleached: columnBleachedPercent, killed: columnKilledPercent };
    this.outcrops?.setCover(columnCoverPercent, columnBleachedPercent, columnKilledPercent);
  }

  /** The reef's outcrops from its model, one for `EXHIBIT_SPRITES.km2PerOutcrop` of reef floor. */
  setReefModel(model: ReefModel, textures?: ReefTextures): void {
    if (this.outcrops) return;
    const reef = this.exhibit.reef;
    const bins = [reef.shallowKm2, reef.midKm2, reef.deepKm2];
    const floorKm2 = reef.columns.map((_, j) => bins.reduce((a, b) => a + (b[j] ?? 0), 0));
    const normals = this.floorMesh.geometry.getAttribute('normal') as THREE.BufferAttribute;
    const floor = (column: number): { at: THREE.Vector3; up: THREE.Vector3 } => ({
      at: new THREE.Vector3().fromArray(this.positions, column * 3),
      up: new THREE.Vector3().fromBufferAttribute(normals, column).normalize(),
    });
    // None where its tallest outcrop would break the surface (M94's review: 12 of 172 parts did, by up to 0.16 units,
    // on Maui's and Molokaʻi's shallowest reef).
    const tallest = (tallestM(model) * EXHIBIT_SPRITES.outcropUnits) / Math.max(...model.outcrops.map((o) => o.widthM));
    const places = outcropPlaces(reef, floorKm2, floor, EXHIBIT_SPRITES.outcropUnits, model.outcrops.length, TOP - tallest, EXHIBIT_SPRITES.km2PerOutcrop, true);
    // Each sunk until no point of its base stands clear of the floor (M104).
    this.outcrops = new ReefOutcrops(model, seatedPlaces(model, places, EXHIBIT_SPRITES.outcropUnits, (x, z) => this.floorAt(x, z)), EXHIBIT_SPRITES.outcropUnits);
    if (textures) this.outcrops.setTextures(textures);
    if (this.lastCover) this.outcrops.setCover(this.lastCover.cover, this.lastCover.bleached, this.lastCover.killed);
    this.outcrops.setLight(...this.lastTint);
    this.group.add(this.outcrops.group);
  }

  /** The reef's fish on a day, each kind's in `FISH_KINDS` order. */
  setFish(counts: readonly KindCounts[]): void {
    this.fish.setCounts(counts);
  }

  /** The school followed, by its seed, drawn whole whatever the level of detail (`FishSchools.hold`); null for none. */
  holdSchool(seed: number | null): void {
    this.fish.hold(seed);
  }

  /** The reef's fish where their day has them at `hour`, moved on by `seconds` of swimming, still within themselves when `calm`; the whales and the honu with them. */
  swimFish(hour: number, dayLengthHours: number, seconds: number, calm = false): void {
    this.fish.swim(hour, dayLengthHours, seconds, calm);
    this.whales.swim(seconds, calm);
    this.turtles.swim(hour, this.duskHour, seconds, calm);
    this.seals.swim(seconds, calm);
  }

  /** The hour civil twilight ends on the day, the sun's (`civilDuskHour`), for the honu's evening (M104). */
  setDusk(hour: number): void {
    this.duskHour = hour;
  }

  /** The honu's sprites on a day (M104), `honuSprites`'. */
  setTurtles(sprites: HonuSprite[]): void {
    this.turtles.set(sprites);
  }

  /** The monk seals' sprites on a day (M145), `sealSprites`'. */
  setSeals(sprites: SealSprite[]): void {
    this.seals.set(sprites);
  }

  /** The seals drawn now, for the tests. */
  sealsDrawn(): SealDrawn[] {
    return this.seals.drawn();
  }

  /**
   * The whale, the honu or the seal under a point on the screen, whichever's middle is nearest the tap for its size
   * (`tapNearness`; M152, measured in the built game: three of seven seals tapped on a phone were taken by a whale or a
   * honu tried first, a whale's box its whole picture); null for none, the fish then tried.
   */
  pickAnimal(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): { whale: number } | { honu: { adult: boolean; index: number } } | { seal: number } | null {
    const whale = this.whales.pick(camera, x, y, widthPx, heightPx, withinPx);
    const honu = this.turtles.pick(camera, x, y, widthPx, heightPx, withinPx);
    const seal = this.seals.pick(camera, x, y, widthPx, heightPx, withinPx);
    const honuDrawn = honu ? this.turtles.at(honu.adult, honu.index) : null;
    const sealDrawn = seal >= 0 ? this.seals.at(seal) : null;
    const found = [
      whale >= 0 ? { near: this.whales.tapNearness(camera, whale, x, y, widthPx, heightPx, withinPx), pick: { whale } } : null,
      honu && honuDrawn ? { near: tapNearness(honuDrawn, camera, x, y, widthPx, heightPx, withinPx), pick: { honu: { adult: honu.adult, index: honu.index } } } : null,
      sealDrawn ? { near: tapNearness(sealDrawn, camera, x, y, widthPx, heightPx, withinPx), pick: { seal } } : null,
    ].filter((f) => f !== null).sort((a, b) => a.near - b.near);
    return found[0]?.pick ?? null;
  }

  /** The seal under a point on the screen, its sprite's key; -1 for none (M152). */
  pickSeal(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return this.seals.pick(camera, x, y, widthPx, heightPx, withinPx);
  }

  /**
   * A seal as the card tells it: as drawn, the real depth of the bottom it forages over (the depth its way was found
   * at, the sources' foraging depth; its own height over the floor is drawn, not real), and the day's sprites by stage;
   * null where it is not drawn.
   */
  sealAt(key: number): (SealDrawn & { depthM: number; sprites: ReturnType<ExhibitSeals['counts']> }) | null {
    const s = this.seals.at(key);
    if (!s) return null;
    return { ...s, depthM: this.exhibit.depthAt((TOP - this.floorAt(s.middle.x, s.middle.z)) / H), sprites: this.seals.counts() };
  }

  /** The honu under a point on the screen, null for none (M104). */
  pickTurtle(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): HonuSprite | null {
    return this.turtles.pick(camera, x, y, widthPx, heightPx, withinPx);
  }

  /** A honu as the card tells it: as drawn, its real depth now, how far apart its places are drawn, km, its hours, and the sprites drawn; null where it is not drawn. */
  turtleAt(adult: boolean, index: number): (HonuDrawn & { depthM: number; wayKm: number; hours: { leave: number; back: number }; sprites: { young: number; adults: number } }) | null {
    const h = this.turtles.at(adult, index);
    if (!h) return null;
    const share = (x: number): number => (x + W / 2) / W;
    const wayKm = Math.abs(this.exhibit.kmAt(share(h.places.rest.x)) - this.exhibit.kmAt(share(h.places.forage.x)));
    return { ...h, depthM: this.exhibit.depthAt((TOP - h.middle.y) / H), wayKm, hours: honuHours(h.places, this.duskHour), sprites: this.turtles.counts() };
  }

  /** The honu drawn now, for the tests. */
  turtlesDrawn(): HonuDrawn[] {
    return this.turtles.drawn();
  }

  /** The humpbacks' sprites on a day (M99): the whales in Hawaii's waters and their calves, `whaleSpritesOf`'s. */
  setWhales(sprites: { whales: number; calves: number }): void {
    this.whales.set(sprites);
  }

  /** The whale under a point on the screen, -1 for none (M99). */
  pickWhale(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return this.whales.pick(camera, x, y, widthPx, heightPx, withinPx);
  }

  /** A whale as the card tells it: its middle, its real depth, its calf beside it, and the sprites drawn; null where it is not drawn. */
  whaleAt(index: number): { middle: THREE.Vector3; depthM: number; calf: boolean; sprites: { whales: number; calves: number } } | null {
    const w = this.whales.at(index);
    if (!w) return null;
    return { middle: w.whale.middle, depthM: this.exhibit.depthAt((TOP - w.whale.middle.y) / H), calf: w.calf !== null, sprites: this.whales.counts() };
  }

  /** Where a tag's mark stands (M156): over a school of its kind on its island, or one of its kind drawn; null where none is. */
  markAt(target: MarkTarget, remembered: number | null): THREE.Vector3 | null {
    if ('island' in target) {
      const kind = FISH_KINDS.findIndex((k) => k.species === target.species);
      return kind < 0 ? null : schoolMark(this.fish, kind, target.island, remembered);
    }
    // Of its own stage, one the camera sees, kept while it is seen (`standIn`, M161), shown more than half (M156's
    // review: the first drawn changed as they came and went, and a mark stood over a sprite fading out).
    const key = JSON.stringify(target);
    const seen = (d: Pickable): boolean => !this.markCamera || seenBy(d, this.markCamera, this.markVisible);
    const one = <T extends Pickable>(drawn: readonly T[], n: (d: T) => number): THREE.Vector3 | null => {
      const d = standIn(drawn, n, seen, this.marked.get(key));
      if (d) this.marked.set(key, n(d));
      return d?.middle ?? null;
    };
    if (target.species === 'honu') return one(this.turtles.drawn().filter((h) => h.sprite.adult === target.adult), (h) => h.sprite.index);
    if (target.species === 'monkSeal') return one(this.seals.drawn().filter((s) => s.sprite.stage === target.stage), (s) => s.sprite.key);
    // The whole tank draws no hawksbill and no tiger shark (M203): their tags marked in the habitats that draw them.
    if (target.species !== 'humpback') return null;
    return one(this.whales.drawn().filter((w) => !w.calf), (w) => w.index);
  }

  /** The whales drawn now, for the tests. */
  whalesDrawn(): ReturnType<ExhibitWhales['drawn']> {
    return this.whales.drawn();
  }

  /** The surface's shimmer and the caustics moved on by `seconds` of the player's time. */
  drift(seconds: number): void {
    this.surface.uTime.value = (this.surface.uTime.value + seconds) % 3600;
    const c = this.causticDrift.value;
    const step = seconds * (W / 320);
    const [[ax, ay], [bx, by]] = CAUSTIC_DIRECTIONS;
    const tile = EXHIBIT_TEXEL * SKIN_TILE_PX;
    const wrap = (v: number): number => ((v % tile) + tile) % tile;
    c.set(wrap(c.x + ax * step), wrap(c.y + ay * step), wrap(c.z + bx * step), wrap(c.w + by * step));
  }

  /**
   * Sizes the labels to their pixel heights and the fish for the camera, their level of detail eased over `seconds` (at
   * once when not given) and measured over the part of the canvas the panels leave (`FishSchools.fit`); turns the
   * whales and the honu the way the camera sees them move (M142); declutters the ruler.
   */
  fitLabels(camera: THREE.PerspectiveCamera, canvasPx: number, seconds = Infinity, visible?: Visible): void {
    this.fish.fit(camera, canvasPx, EXHIBIT_SPRITES.minPx, seconds, visible);
    [this.markCamera, this.markVisible] = [camera, visible];
    this.whales.face(camera);
    this.turtles.face(camera);
    this.seals.face(camera);
    const p11 = camera.projectionMatrix.elements[5] ?? 1;
    const key = p11 * canvasPx;
    if (key !== this.labelsFitFor) {
      this.labelsFitFor = key;
      this.group.traverse((o) => {
        if (!(o instanceof THREE.Sprite) || typeof o.userData.labelPx !== 'number') return;
        const h = (2 * o.userData.labelPx) / key;
        o.scale.set(h * (o.userData.aspect as number), h, 1);
      });
    }
    const e = camera.matrixWorld.elements;
    let same = this.rulerFor[16] === p11 && this.rulerFor[17] === canvasPx;
    for (let i = 0; i < 16 && same; i++) same = e[i] === this.rulerFor[i];
    if (same) return;
    this.rulerFor.set(e);
    this.rulerFor[16] = p11;
    this.rulerFor[17] = canvasPx;
    const sprites = this.ruler.children.filter((o): o is THREE.Sprite => o instanceof THREE.Sprite && typeof o.userData.depthM === 'number');
    const shown = placeRulerLabels(sprites.map((o) => {
      o.getWorldPosition(this.rulerPoint).project(camera);
      return { depthM: o.userData.depthM as number, screenY: ((1 - this.rulerPoint.y) / 2) * canvasPx, heightPx: o.userData.labelPx as number, keep: o.userData.keep === true };
    }));
    sprites.forEach((o, i) => { o.visible = shown[i] ?? true; });
  }

  dispose(): void {
    this.fish.dispose();
    this.whales.dispose();
    this.turtles.dispose();
    this.seals.dispose();
    if (this.outcrops) {
      this.group.remove(this.outcrops.group);
      this.outcrops.dispose();
    }
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Line || o instanceof THREE.Sprite || o instanceof THREE.Points) {
        if (!(o instanceof THREE.Sprite)) o.geometry.dispose();
        const m = o.material as THREE.Material & { map?: THREE.Texture | null };
        m.map?.dispose();
        m.dispose();
      }
    });
  }
}
